import { Box, Button, ButtonGroup, Flex, GridItem, Heading, useBoolean } from '@chakra-ui/react';
import { deleteUploadImage } from 'api/deleteUploadImage';
import { AddImageToFolderRequest, addImageToFolder, deleteFolder } from 'api/mediaFolders';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as ImageIcon } from 'assets/icons/folderImage.svg';
import { IMAGE_CREATED, IMAGE_DELETED, IMAGE_FOLDER_DELETED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { GET_USER_MEDIA_API } from 'modules/User';
import { useToast } from 'modules/common/components/Toast';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';

import DeleteAlertDialog from '../DeleteAlertDialog';
import { ImageUpload } from '../ImageUpload';
import { UploadImagesData } from '../UploadMenu.helper';
import { useUploadMenu } from '../UploadMenuContext';
import { FolderImages } from './FolderImages';
import { FolderProps, createWidgetData, getImagesFromFolder } from './Folders.helper';
import { Footer } from './Footer';

interface UploadErrorResponse {
  message: string;
}
interface ImageContainerProps {
  uploadImages: UploadImagesData;
  selectedFolder: FolderProps;
  selectFolder: (folder: FolderProps) => void;
  usersFolders: FolderProps[];
  selectedImages: string[];
  selectImages: (imageId: string[]) => void;
  closeModal: () => void;
}

interface GenericErrorProps {
  message: string;
}

interface OnDeleteAction {
  title: string;
  message: string;
  onDelete: () => void;
}

/**
 * Container to handle all images actions
 * @param ImageContainerProps
 * @returns ReactElement
 */
export const ImageContainer = ({
  uploadImages = [],
  selectedFolder,
  selectFolder,
  usersFolders,
  selectedImages,
  selectImages,
  closeModal,
}: ImageContainerProps) => {
  const toast = useToast();
  const [openDelete, toggleDeleteModal] = useBoolean();
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.imagesMenu.folders' });
  const { mutate } = useSWRConfig();
  const { addUploadImageWidget } = useUploadMenu();
  const [ondDeleteAction, setOnDeleteAction] = useState<OnDeleteAction>();

  /**
   * Refresh data
   */
  const refreshFolder = useCallback(async () => {
    await mutate(GET_USER_MEDIA_API);
    // reset selection
    selectImages([]);
  }, [mutate, selectImages]);

  const genericErrorToast = useCallback(
    ({ message }: GenericErrorProps) => {
      toast({
        title: t('genericError'),
        description: message,
        status: 'error',
      });
    },
    [t, toast],
  );

  /**
   *  After delete a folder move to all uploads
   */
  const onDeleteFolder = async () => {
    if (selectedFolder.value) {
      const results = await deleteFolder(selectedFolder.value);

      if (results.status === 'fail') {
        genericErrorToast({
          message: results.message,
        });
        return;
      }

      // delete all images from folder
      await Promise.all(
        getImagesFromFolder(selectedFolder?.medias || '').map(async (image: string) => {
          try {
            return deleteUploadImage(image);
          } catch (error) {
            const deleteUploadErr = error as UploadErrorResponse;
            genericErrorToast({
              message: deleteUploadErr.message,
            });
          }
        }),
      );

      toast({
        title: `"${selectedFolder.label}" ${t('foldersDeleted')}`,
        position: 'top',
      });
      selectFolder({
        value: '',
        label: t('allUploads'),
        icon: <ImageIcon />,
      });
      refreshFolder();

      Mixpanel.track(IMAGE_FOLDER_DELETED.eventName);
    }
  };

  const addToPage = useCallback(() => {
    let added = 0;
    selectedImages.forEach((image) => {
      try {
        const element = document.getElementById(image) as HTMLImageElement;

        const widget = createWidgetData(element);
        if (widget) {
          addUploadImageWidget(widget);
          added++;
        }
      } catch (error) {
        const deleteUploadErr = error as UploadErrorResponse;

        genericErrorToast({
          message: deleteUploadErr.message,
        });
      }
    });

    if (added) {
      toast({
        title: `${added} ${t('addedToPage')}`,
        position: 'top',
      });

      Mixpanel.track(IMAGE_CREATED.eventName, {
        from: IMAGE_CREATED.from.Upload,
      });
      closeModal();
    }
  }, [addUploadImageWidget, closeModal, genericErrorToast, selectedImages, t, toast]);

  /**
   * Remove images from folder
   */
  const onDeleteImages = useCallback(async () => {
    let imagesUploaded = 0;
    await Promise.all(
      selectedImages.map(async (image) => {
        try {
          imagesUploaded++;
          return deleteUploadImage(image);
        } catch (error) {
          const deleteUploadErr = error as UploadErrorResponse;
          genericErrorToast({
            message: deleteUploadErr.message,
          });
        }
      }),
    );

    toast({
      title: `${imagesUploaded} ${t('deletedImages')}`,
      position: 'top',
    });

    Mixpanel.track(IMAGE_DELETED.eventName, {
      from: IMAGE_DELETED.from.UploadModal,
    });

    let folder = selectedFolder;
    // if it has a selected folder we should remove the medias from it
    if (folder?.medias) {
      const getFolderImages = getImagesFromFolder(folder.medias || '');
      const removeImagesFromFolder = getFolderImages.filter((i: string) => !selectedImages.includes(i));

      folder = {
        ...folder,
        medias: JSON.stringify(removeImagesFromFolder),
      };
    }
    delete folder.icon;
    // update folder with the removed files
    await addImageToFolder({ folder });

    refreshFolder();
    selectFolder(folder);
  }, [genericErrorToast, refreshFolder, selectFolder, selectedFolder, selectedImages, t, toast]);

  /**
   * handler to select/unselect images
   */
  const setSelectedImages = useCallback(
    (imageId: string) => {
      let selected: string[] = [];
      const index = selectedImages.findIndex((item) => item === imageId);
      if (index === -1) {
        selected = [...selectedImages, imageId];
      } else {
        selected = selectedImages.filter((item) => item !== imageId);
      }
      selectImages(selected);
    },
    [selectImages, selectedImages],
  );

  /**
   * handler to upload files and add to folders
   */
  const onUpload = useCallback(
    async ({ imageNames }: { imageNames: string[] }) => {
      let folder = selectedFolder;
      // if it has a selected folder we should remove the medias from it
      if (folder?.medias) {
        const getFolderImages = getImagesFromFolder(folder.medias || '');

        folder = {
          ...folder,
          medias: JSON.stringify([...getFolderImages, ...imageNames]),
        };
      } else {
        folder = {
          ...folder,
          medias: JSON.stringify(imageNames),
        };
      }

      delete folder.icon;

      const results = await addImageToFolder({ folder });

      if (results.status === 'ok') {
        toast({
          title: `${imageNames.length} ${t('onUploadImages')} "${selectedFolder?.label}"`,
          position: 'top',
        });
      }

      // refresh data
      refreshFolder();
      selectFolder(folder);
    },
    [refreshFolder, selectFolder, selectedFolder, t, toast],
  );

  /**
   * handler to move images to folders
   */
  const onMoveToFolder = useCallback(
    async (folder: FolderProps) => {
      // if it has a selected folder we should remove the medias from it
      let fromFolder = selectedFolder;
      if (fromFolder?.medias) {
        const getFolderImages = getImagesFromFolder(fromFolder.medias || '');
        const removeImagesFromFolder = getFolderImages.filter((i: string) => !selectedImages.includes(i));

        fromFolder = {
          ...fromFolder,
          medias: JSON.stringify(removeImagesFromFolder),
        };
      }

      // remove unused fields
      delete fromFolder.icon;
      delete folder.icon;

      const addMediaToNewFolder = JSON.stringify([...getImagesFromFolder(folder.medias || ''), ...selectedImages]);

      let request: AddImageToFolderRequest = {
        folder: {
          ...folder,
          medias: addMediaToNewFolder,
        },
        from: fromFolder,
      };

      // when the default folder(All uploads) is selected we just remove the images from the folder
      if (folder.value === '') {
        request = {
          folder: {
            label: fromFolder.label,
            value: fromFolder.value,
            medias: fromFolder.medias || '',
          },
        };
      }

      const results = await addImageToFolder(request);

      if (results.status === 'fail') {
        genericErrorToast({
          message: results.message,
        });
        return;
      }

      toast({
        title: `${selectedImages.length} ${t('onMoveImages', { from: fromFolder?.label, to: folder.label })}`,
        position: 'top',
      });

      refreshFolder();
      selectFolder(fromFolder);
    },
    [genericErrorToast, refreshFolder, selectFolder, selectedFolder, selectedImages, t, toast],
  );

  /**
   * list of delete actions
   */
  const onDeleteAction = {
    image: {
      title: t('deleteImageTitle'),
      message: t('deleteImageMessage'),
      onDelete: onDeleteImages,
    },
    folder: {
      title: t('deleteFolderTitle'),
      message: t('deleteFolderMessage'),
      onDelete: onDeleteFolder,
    },
  };

  /**
   * Handler the confirmation modal for folder deletion
   */
  const handlerOnDeleteFolder = () => {
    setOnDeleteAction(onDeleteAction.folder);
    toggleDeleteModal.on();
  };

  /**
   * Handler the confirmation modal for images deletion
   */
  const handlerOnDeleteImage = () => {
    setOnDeleteAction(onDeleteAction.image);
    toggleDeleteModal.on();
  };

  /**
   * this useEffect is to clean the selected images and a new folder is selected
   */
  useEffect(() => {
    if (selectedFolder) {
      selectImages([]);
    }
  }, [selectImages, selectedFolder]);

  return (
    <>
      {ondDeleteAction && (
        <DeleteAlertDialog isOpen={openDelete} onClose={toggleDeleteModal.off} {...ondDeleteAction} />
      )}
      <GridItem area={'main'} data-testid='grid-main' px={9} pt={9}>
        <Box>
          <Flex justifyContent='space-between'>
            <Heading size={'md'}>{selectedFolder.label}</Heading>
            <ButtonGroup>
              {selectedFolder.value && (
                <Button
                  leftIcon={<DeleteIcon width='20px' height='20px' />}
                  variant='outline'
                  size='sm'
                  onClick={handlerOnDeleteFolder}
                  data-testid='delete-folder-btn'
                >
                  {t('deleteFolder')}
                </Button>
              )}
              <ImageUpload onlyUploadButton onUpload={onUpload} />
            </ButtonGroup>
          </Flex>
          {uploadImages.length === 0 && <ImageUpload onUpload={onUpload} />}

          {uploadImages.length > 0 && (
            <ImageUpload dragDropContainer onUpload={onUpload}>
              <Box style={{ overflow: 'auto', height: 'calc(64vh)' }}>
                <Flex maxH='full' flexDir='column' overflowY='auto'>
                  <FolderImages
                    uploadImages={uploadImages}
                    setSelectedImages={setSelectedImages}
                    selectedImages={selectedImages}
                  />
                </Flex>
              </Box>
            </ImageUpload>
          )}
        </Box>
      </GridItem>
      <Footer
        onDelete={handlerOnDeleteImage}
        addToPage={addToPage}
        onMoveToFolder={onMoveToFolder}
        selectImages={selectImages}
        selectedImages={selectedImages}
        userFolders={usersFolders}
        selectedFolder={selectedFolder}
      />
    </>
  );
};
